"use client";

// Портал: список приложений по всем эккаунтам, где юзер админ. В строке —
// иконка, имя, эккаунт-владелец и режим публикации.
import Link from "next/link";
import { Plus } from "lucide-react";

import { Avatar } from "@/components/avatar";
import { DevEmpty, DevResourceGate } from "@/components/dev/dev-state";
import { PublicationBadge } from "@/components/dev/publication-badge";
import { useDevResource } from "@/components/dev/use-dev-resource";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";
import { listDevRoots, type DevRootCard } from "@/modules/dev/api";
import { DEV_APPS_PATH, devAppPath } from "@/modules/dev/paths";

export default function DevAppsPage() {
  const t = useTranslation();
  const resource = useDevResource<DevRootCard[]>(() => listDevRoots(), []);
  const roots = resource.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-m-text">{t("dev.nav.apps")}</h1>
        <Link href={`${DEV_APPS_PATH}/new`}>
          <Button size="sm">
            <Plus className="mr-1.5 h-4 w-4" />
            {t("dev.list.create")}
          </Button>
        </Link>
      </div>
      {resource.status !== "ready" ? (
        <DevResourceGate
          status={resource.status}
          error={resource.error}
          onRetry={resource.reload}
        />
      ) : roots.length === 0 ? (
        <DevEmpty text={t("dev.list.empty")}>
          <Link href={`${DEV_APPS_PATH}/new`}>
            <Button size="sm">
              <Plus className="mr-1.5 h-4 w-4" />
              {t("dev.list.create")}
            </Button>
          </Link>
        </DevEmpty>
      ) : (
        <div className="space-y-3">
          {roots.map((root) => (
            <Link
              key={root.id}
              href={devAppPath(root.id)}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-m-divider bg-m-surface p-4 hover:bg-m-surface-light"
            >
              <Avatar url={root.icon_url} name={root.name} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-m-text">{root.name}</p>
                <p className="text-sm text-m-text-hint">
                  {t("dev.list.owner").replace("{owner}", root.owner_account_name ?? "")}
                  {" · "}{root.slug}
                </p>
              </div>
              <PublicationBadge root={root} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
